import { memo } from 'react'

import { cn } from '@/lib/utils'

import type { Worker } from '../types'
import { formatDistance } from '../utils/distance'
import { WORKER_STATUS_LABEL } from '../utils/job-status'
import { WORKER_STATUS_VISUAL } from './status-meta'

export interface NearestInfo {
  rank: number
  distanceKm: number | null
  etaMinutes: number | null
}

/** Marker body: coloured disc + status icon (never colour alone), optional distance/ETA label. */
export const WorkerMarker = memo(function WorkerMarker({
  worker,
  isSelected,
  nearest,
  dimmed,
  onSelect,
}: {
  worker: Worker
  isSelected: boolean
  nearest: NearestInfo | null
  dimmed: boolean
  onSelect: (workerId: string) => void
}) {
  const visual = WORKER_STATUS_VISUAL[worker.status]
  const Icon = visual.icon
  const nearestLabel = nearest
    ? `${formatDistance(nearest.distanceKm)}${nearest.etaMinutes != null ? ` · ${nearest.etaMinutes} min` : ''}`
    : null
  const label = nearestLabel ? `${nearest?.rank === 0 ? '★ ' : ''}${nearestLabel}` : null

  return (
    // translateY(50%) re-centres the disc on the position (host anchors bottom-centre).
    <div className="relative" style={{ transform: 'translateY(50%)' }}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onSelect(worker.id)
        }}
        aria-label={`${worker.name}, ${WORKER_STATUS_LABEL[worker.status]}${nearestLabel ? `, ${nearestLabel} away` : ''}. Show details`}
        aria-pressed={isSelected}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-white shadow-md transition-[transform,opacity]',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#169b62]/60',
          visual.solid,
          (isSelected || nearest) && 'scale-110 ring-4 ring-white/80',
          nearest?.rank === 0 && 'ring-[#169b62]',
          dimmed && 'opacity-[0.35]',
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
      {label && !dimmed ? (
        <span className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#20352b] shadow">
          {label}
        </span>
      ) : null}
    </div>
  )
})
